/**
 * Here we map sections based on their componentName
 */

import { Box, Container } from '@everlywell/ui-kit';
import CtaBanner from 'components/CtaBanner';
import ParseBannerWithCta from 'components/CtaBanner/CtaBanner.parser';
import FaqV2, { parseFaqV2Data } from 'components/FaqV2';
import GridCardList, { parseGridCardListData } from 'components/GridCardList';
import CompareTestsSection from 'components/PageSections/CompareTestsSection';
import DigitalResultsSection from 'components/PageSections/DigitalResultsSection';
import ProductCarousel, {
  parseProductCarouselData,
} from 'components/ProductCarousel';
import StepCardList, { parseStepCardListData } from 'components/StepCardList';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { logError } from 'utils/helpers';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';

import PDPReviewsSection from './PDPReviewsSection';

export type SectionProps = {
  data: Queries.pdpSectionFragment;
  productData: Queries.pdpProductFragment;
};

const useComponentViewedTracking = (data: {
  label: string;
  component: string;
}) => {
  const { ref } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (!data.label || !data.component) {
        logError(
          'label and component are required for useComponentViewedTracking',
          {
            component: 'PDP/Section',
            method: 'useComponentViewedTracking',
          },
        );
      }
      if (inView) {
        analytics.track({
          event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
          data,
        });
      }
    },
  });

  return ref;
};

/**
 * Renders a section based on its componentName
 * @param props
 * @returns
 */

export default function Section(props: SectionProps) {
  const { data: section, productData } = props;
  const clipPathStyles = {
    // if first section, background curve is on the bottom
    '&:first-of-type:not(:last-of-type)': {
      clipPath: 'ellipse(180% 60% at 50% 40%)',
    },
    // if middle section, background curve is on the top and bottom
    '&:not(:last-of-type):not(:first-of-type)': {
      clipPath: 'ellipse(180% 50% at 50% 50%)',
    },
    // if last section, background curve is on the top
    '&:last-of-type:not(:first-of-type)': {
      clipPath: 'ellipse(180% 60% at 50% 60%)',
    },
  };

  const sectionStyles = {
    bg: section.metadata?.theme?.backgroundColor ?? undefined,
    py: section.metadata?.theme?.backgroundColor ? 16 : 0,
    display: 'flex',
    justifyContent: 'center',
    ...(section.metadata?.theme?.backgroundColor ? clipPathStyles : {}),
  };

  const { advertisedPromos } = useAdvertisedPromos();

  const ref = useComponentViewedTracking({
    label:
      section.displayName ??
      `Section ${section.contentful_id}: Display Name Missing`,
    component: section.componentName ?? '',
  });

  switch (section.componentName) {
    case 'PDPReviewsSection':
      return (
        <Box
          as="section"
          ref={ref}
          key={section.contentful_id}
          sx={sectionStyles}
        >
          {/* note: be careful trying to combine Box and Container - weird things start to happen */}
          <Container size={['sm', 'sm', 'xl']} overflow="clip">
            {productData && (
              <PDPReviewsSection {...section} productData={productData} />
            )}
          </Container>
        </Box>
      );
    case 'DigitalResultsSection':
      return (
        <Box as="section" key={section.contentful_id} sx={sectionStyles}>
          <DigitalResultsSection data={section} />
        </Box>
      );
    case 'StepCardList':
      const stepCardListData = parseStepCardListData(section);
      if (!stepCardListData) {
        return null;
      }
      return (
        <Box
          as="section"
          ref={ref}
          key={section.contentful_id}
          sx={sectionStyles}
        >
          <StepCardList {...stepCardListData} />
        </Box>
      );
    case 'GridCardList':
      const gridCardListData = parseGridCardListData(section);
      if (!gridCardListData) {
        return null;
      }
      return (
        <Box
          as="section"
          ref={ref}
          key={section.contentful_id}
          sx={sectionStyles}
        >
          <GridCardList {...gridCardListData} />
        </Box>
      );
    case 'CompareTestsSection':
      return (
        <Box
          as="section"
          ref={ref}
          key={section.contentful_id}
          sx={sectionStyles}
        >
          <Container size={['sm', 'sm', 'xl']} overflow="clip">
            <CompareTestsSection data={section} productData={productData} />
          </Container>
        </Box>
      );
    case 'FaqV2':
      const faqData = parseFaqV2Data(section);
      if (!faqData) {
        return null;
      }
      return (
        <Box
          as="section"
          ref={ref}
          key={section.contentful_id}
          sx={sectionStyles}
          data-testid="faqSection"
        >
          <FaqV2 {...faqData} />
        </Box>
      );
    case 'CtaBanner':
      const ctaBannerData = ParseBannerWithCta(section);

      if (!ctaBannerData) {
        return null;
      }

      return (
        <Box
          as="section"
          ref={ref}
          key={section.contentful_id}
          sx={sectionStyles}
        >
          <CtaBanner {...ctaBannerData} useBackgroundImage />
        </Box>
      );

    case 'ProductCarousel':
      const productCarouselData = parseProductCarouselData(section);
      if (!productCarouselData) {
        return null;
      }
      return (
        // No <Box> wrapper since it is handled inside of the ProductCarousel
        <ProductCarousel
          {...productCarouselData}
          advertisedPromos={advertisedPromos}
        />
      );
    default:
      return null;
  }
}
